type EnneagramAnswers = Record<string, number>

export function assessmentQuestions() {
  return [
    'I am a perfectionist.',
    'My relationships with others are what my life is about.',
    'I put work first.',
    'I daydream about being in love.',
    'I have a hard time showing emotions.',
    'Fear of being taken advantage of keeps me from being more trusting.',
    'I must always be having new experiences.',
    'I naturally emerge as a leader.',
    'When other people are arguing, I leave the room.',
    'I strive for efficiency.',
    'I have difficulty saying no.',
    'I like to stand out.',
    'I really enjoy feeling bitter sweet.',
    'I spend hours alone with my hobbies.',
    'I get input from others before I make a decision.',
    'I can keep a conversation going with anyone about anything.',
    'I like a conversation where no one agrees.',
    'I keep my thoughts to myself, to prevent trouble.',
    'I often have to redo other peoples work.',
    'I get lots of satisfaction from helping others achieve their goals.',
    'It is good to wake up to a full day of planned activities.',
    'I cry.',
    'I spend most of my time trying to understand things.',
    'I conform.',
    'I am uninhibited.',
    'I want people to tell me the truth, not spare my feelings.',
    'I am very accepting and flexible.',
    'I keep my belongings in order.',
    'I put family first.',
    'Money is important to my happiness.',
    'I side with the rebels over the establishment.',
    'I like mental challenges.',
    'I am loyal.',
    'I always try to break the tension with a good joke.',
    'I prefer it when leaders are decisive.',
    'I avoid confrontation.',
  ]
}

function getScore(answers: Record<string, number>, answerKeys: Array<number>) {
  return answerKeys
    .map(key => answers[String(key)])
    .reduce((total, curr) => total + curr, 0)
}

export function getTypeScores(answers: EnneagramAnswers): EnneagramAnswers {
  const typeScores = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
  }

  typeScores[1] = getScore(answers, [0, 9, 18, 27])
  typeScores[2] = getScore(answers, [1, 10, 19, 28])
  typeScores[3] = getScore(answers, [2, 11, 20, 29])
  typeScores[4] = getScore(answers, [3, 12, 21, 30])
  typeScores[5] = getScore(answers, [4, 13, 22, 31])
  typeScores[6] = getScore(answers, [5, 14, 23, 32])
  typeScores[7] = getScore(answers, [6, 15, 24, 33])
  typeScores[8] = getScore(answers, [7, 16, 25, 34])
  typeScores[9] = getScore(answers, [8, 17, 26, 35])

  return typeScores
}

export function getType(answers: EnneagramAnswers): string {
  const scores = getTypeScores(answers)
  let type = ''
  let maxScore = 0

  for (const currType in scores) {
    if (scores[currType] >= maxScore) {
      maxScore = scores[currType]
      type = currType
    }
  }

  return type
}

export function getTypeFromScores(scores: EnneagramAnswers): string {
  let type = ''
  let maxScore = 0

  for (const currType in scores) {
    if (scores[currType] >= maxScore) {
      maxScore = scores[currType]
      type = currType
    }
  }

  return type
}

export function getTitle(type: string) {
  switch (type) {
    case '1':
      return 'Idealist'
    case '2':
      return 'Caregiver'
    case '3':
      return 'Performer'
    case '4':
      return 'Creative'
    case '5':
      return 'Thinker'
    case '6':
      return 'Loyalist'
    case '7':
      return 'Adventurer'
    case '8':
      return 'Protector'
    case '9':
      return 'Peacekeeper'
    default:
      return ''
  }
}

export function getDescription(type: string, profile: { first_name: string }) {
  switch (type) {
    case '1':
      return `As a Type One, ${profile.first_name} tends to be rational, principled, and judicial. ${profile.first_name} usually has a desire for justice and equality and is motivated by a strong sense of right and wrong.`
    case '2':
      return `As a Type Two, ${profile.first_name} tends to be generous, altruistic, and empathetic. ${profile.first_name} is likely a hardworking and motivated person with a drive to connect with the world.`
    case '3':
      return `As a Type Three, ${profile.first_name} tends to be ambitious, adaptable, and enthusiastic. ${profile.first_name} is generally driven and loves to set and accomplish goals.`
    case '4':
      return `As a Type Four, ${profile.first_name} tends to be creative, sensitive, and expressive. ${profile.first_name} generally likes to be unique and seeks to find a distinct identity.`
    case '5':
      return `As a Type Five, ${profile.first_name} tends to be curious, independent, and observant. ${profile.first_name} generally loves to pursue knowledge and seeks a deeper understanding of the world.`
    case '6':
      return `As a Type Six, ${profile.first_name} tends to be engaging, hard-working, and responsible. ${profile.first_name} generally wants to feel safe and avoid outside threats.`
    case '7':
      return `As a Type Seven, ${profile.first_name} tends to be enthusiastic, adventurous, and spontaneous. ${profile.first_name} is likely people-oriented and generally enjoys pursuing new relationships.`
    case '8':
      return `As a Type Eight, ${profile.first_name} tends to be self-confident, powerful, and assertive. ${profile.first_name} generally enjoys engaging in debates and making difficult decisions.`
    case '9':
      return `As a Type Nine, ${profile.first_name} tends to be accepting, optimistic, and adaptive. ${profile.first_name} generally likes peace and avoids conflict.`
    default:
      return ''
  }
}

export function getDescriptionGeneric(type: string) {
  switch (type) {
    case '1':
      return 'As a Type One, you tend to be rational, principled, and judicial. You usually have a desire for justice and equality and are motivated by a strong sense of right and wrong.'
    case '2':
      return 'As a Type Two, you tend to be generous, altruistic, and empathetic. You are likely a hardworking and motivated person with a drive to connect with the world.'
    case '3':
      return 'As a Type Three, you tend to be ambitious, adaptable, and enthusiastic. You are generally driven and love to set and accomplish goals.'
    case '4':
      return 'As a Type Four, you tend to be creative, sensitive, and expressive. You generally like to be unique and seek to find a distinct identity.'
    case '5':
      return 'As a Type Five, you tend to be curious, independent, and observant. You generally love to pursue knowledge and seek a deeper understanding of the world.'
    case '6':
      return 'As a Type Six, you tend to be engaging, hard-working, and responsible. You generally want to feel safe and avoid outside threats.'
    case '7':
      return 'As a Type Seven, you tend to be enthusiastic, adventurous, and spontaneous. You are likely people-oriented and generally enjoy pursuing new relationships.'
    case '8':
      return 'As a Type Eight, you tend to be self-confident, powerful, and assertive. You generally enjoy engaging in debates and making difficult decisions.'
    case '9':
      return 'As a Type Nine, you tend to be accepting, optimistic, and adaptive. You generally like peace and avoid conflict.'
    default:
      return ''
  }
}

export function getDescriptionForGroup(type: string) {
  switch (type) {
    case '1':
      return 'People who are <Enneagram type One> tend to be rational, principled, and judicial. They usually have a desire for justice and equality and are motivated by a strong sense of right and wrong.'
    case '2':
      return 'People who are <Enneagram type Two> tend to be generous, altruistic, and empathetic. They are likely hardworking and motivated people with a drive to connect with the world.'
    case '3':
      return 'People who are <Enneagram type Three> tend to be ambitious, adaptable, and enthusiastic. They are generally driven and love to set and accomplish goals.'
    case '4':
      return 'People who are <Enneagram type Four> tend to be creative, sensitive, and expressive. They generally like to be unique and seek to find a distinct identity.'
    case '5':
      return 'People who are <Enneagram type Five> tend to be curious, independent, and observant. They generally love to pursue knowledge and seek a deeper understanding of the world.'
    case '6':
      return 'People who are <Enneagram type Six> tend to be engaging, hard-working, and responsible. They generally want to feel safe and avoid outside threats.'
    case '7':
      return 'People who are <Enneagram type Seven> tend to be enthusiastic, adventurous, and spontaneous. They are likely people-oriented and generally enjoy pursuing new relationships.'
    case '8':
      return 'People who are <Enneagram type Eight> tend to be self-confident, powerful, and assertive. They generally enjoy engaging in debates and making difficult decisions.'
    case '9':
      return 'People who are <Enneagram type Nine> tend to be accepting, optimistic, and adaptive. They generally like peace and avoid conflict.'
    default:
      return ''
  }
}
